import { runAz } from "./az.js";
import { INSTALLER_GRANT_DESCRIPTION } from "./bootstrap-mi.js";

const ROLE_ASSIGNMENT_API = "2022-04-01";

interface RoleAssignmentRow {
  id: string;
  principalName?: string;
  principalType?: string;
  roleDefinitionName?: string;
  scope?: string;
  description?: string;
  createdOn?: string;
}

/**
 * Find and optionally delete orphaned installer Owner@subscription role assignments.
 * An orphan matches if:
 *   - Its description is the installer tag (precise), OR
 *   - It has no principalName, is ServicePrincipal-typed, Owner role, exactly sub scope (generic).
 * Deletes only when execute=true. Returns all matched candidates regardless.
 */
export async function sweepOrphanOwnerAssignments(opts: {
  subscriptionId: string;
  execute: boolean;
}): Promise<{ id: string; description?: string; createdOn?: string }[]> {
  const subScope = `/subscriptions/${opts.subscriptionId}`;
  const all = JSON.parse(
    (await runAz([
      "role", "assignment", "list", "--all", "--subscription", opts.subscriptionId, "-o", "json",
    ]).catch(() => "[]")) || "[]",
  ) as RoleAssignmentRow[];
  const matched = all.filter((r) => {
    if (r.description === INSTALLER_GRANT_DESCRIPTION) return true;
    return (
      !r.principalName &&
      r.principalType === "ServicePrincipal" &&
      r.roleDefinitionName === "Owner" &&
      r.scope === subScope
    );
  });
  if (opts.execute) {
    for (const r of matched) {
      await runAz([
        "rest", "--method", "delete",
        "--url", `https://management.azure.com${r.id}?api-version=${ROLE_ASSIGNMENT_API}`,
      ]).catch(() => undefined);
    }
  }
  return matched.map((r) => ({ id: r.id, description: r.description, createdOn: r.createdOn }));
}

/**
 * Tear down the installer scaffolding (F3 RETRO §3): ACI → MI → the MI's
 * role assignments. Assignments are deleted BY ARM ID via the REST API —
 * the robust method when the principal may already be gone (F3 §7;
 * `--assignee`/`--ids`/`--name` all fail for a deleted principal). The
 * platform RG and the gateway's own assignments persist.
 */
export async function reapInstaller(opts: {
  subscriptionId: string;
  resourceGroup: string;
  aciName: string;
  miName: string;
  roleAssignmentIds: string[];
  principalId: string;
}): Promise<void> {
  const swallow = async (p: Promise<unknown>): Promise<void> => { try { await p; } catch { /* idempotent */ } };

  await swallow(runAz(["container", "delete", "--name", opts.aciName, "--resource-group", opts.resourceGroup, "--subscription", opts.subscriptionId, "--yes", "--only-show-errors"]));
  await swallow(runAz(["identity", "delete", "--name", opts.miName, "--resource-group", opts.resourceGroup, "--subscription", opts.subscriptionId]));
  for (const id of opts.roleAssignmentIds) {
    await swallow(runAz([
      "rest", "--method", "delete",
      "--url", `https://management.azure.com${id}?api-version=${ROLE_ASSIGNMENT_API}`,
    ]));
  }

  // Belt-and-suspenders: a deleted MI's sub-scope Owner can survive if the captured
  // id list was stale/empty. The assignment record keeps principalId after deletion.
  const survivors = JSON.parse(
    (await runAz([
      "role", "assignment", "list", "--all", "--subscription", opts.subscriptionId,
      "--query", `[?principalId=='${opts.principalId}' && roleDefinitionName=='Owner'].id`, "-o", "json",
    ]).catch(() => "[]")) || "[]",
  ) as string[];
  for (const id of survivors) {
    await swallow(runAz(["rest", "--method", "delete", "--url", `https://management.azure.com${id}?api-version=${ROLE_ASSIGNMENT_API}`]));
  }
}
